/**
 * boundaryAlignmentTools — pure geometry, study-area linkage, viewport fitting,
 * snapping, alignment guides, and opt-in orthogonal/square constraint helpers
 * for the Boundary & Study Area workspace (#244).
 *
 * Key invariants:
 *   1. Selected study-area polygons (`site.studyArea.included`) are resolved by
 *      saved `{ kind, id, geometryHash }` and rendered in blue regardless of
 *      normal `layer.visible !== false`, while staying strictly distinct from
 *      canonical site boundary parcels (`siteBoundaries` / `siteBoundary`).
 *   2. Missing or stale study-area pins (outline edited since pinned) are
 *      explicitly surfaced rather than silently re-pinned or promoted into the
 *      legal site boundary.
 *   3. All alignment, snapping, and orthogonal/squaring tools are strictly
 *      opt-in, operate with a preview + undo/cancel + explicit confirmation,
 *      never automatically alter imported/survey boundaries, and validate
 *      resulting polygon topology via `evaluateStudyPolygon`.
 */

import type { SiteBoundaryParcel } from '../types/siteBoundary';
import type { StudyGeometryRef, StudyGeometryRole } from '../types/siteStudyArea';
import {
  evaluateStudyPolygon,
  listStudyAreaCandidates,
  studyGeometryKey,
  studyPolygonChangedMessage,
  type StudyAreaSite,
} from './siteStudyArea';
import {
  materializeSiteBoundaries,
  polygonArea,
  polygonCentroid,
  polygonPerimeter,
} from './siteBoundaryGeometry';

export interface LatLngPoint {
  lat: number;
  lng: number;
}

export interface LatLngBoundsBox {
  north: number;
  south: number;
  east: number;
  west: number;
}

const EARTH_RADIUS_M = 6_371_000;
const DEG_TO_RAD = Math.PI / 180;
const RAD_TO_DEG = 180 / Math.PI;

function isValidPoint(p: unknown): p is LatLngPoint {
  if (!p || typeof p !== 'object') return false;
  const { lat, lng } = p as { lat?: unknown; lng?: unknown };
  return (
    typeof lat === 'number' &&
    typeof lng === 'number' &&
    Number.isFinite(lat) &&
    Number.isFinite(lng) &&
    lat >= -90 &&
    lat <= 90 &&
    lng >= -180 &&
    lng <= 180
  );
}

/** Local equirectangular projection (east = +x meters, north = +y meters). */
export function projectLatLngToMeters(
  point: LatLngPoint,
  origin: LatLngPoint,
): { x: number; y: number } {
  const cosLat = Math.max(1e-6, Math.cos(origin.lat * DEG_TO_RAD));
  const x = (point.lng - origin.lng) * DEG_TO_RAD * EARTH_RADIUS_M * cosLat;
  const y = (point.lat - origin.lat) * DEG_TO_RAD * EARTH_RADIUS_M;
  return { x, y };
}

export function unprojectMetersToLatLng(
  xy: { x: number; y: number },
  origin: LatLngPoint,
): LatLngPoint {
  const cosLat = Math.max(1e-6, Math.cos(origin.lat * DEG_TO_RAD));
  const lat = origin.lat + (xy.y / EARTH_RADIUS_M) * RAD_TO_DEG;
  const lng = origin.lng + (xy.x / (EARTH_RADIUS_M * cosLat)) * RAD_TO_DEG;
  return {
    lat: Number(lat.toFixed(7)),
    lng: Number(lng.toFixed(7)),
  };
}

export function distanceMetersBetween(a: LatLngPoint, b: LatLngPoint): number {
  const { x, y } = projectLatLngToMeters(b, a);
  return Math.hypot(x, y);
}

/** Bearing in degrees [0, 360) clockwise from true North (0 = N, 90 = E). */
export function normalizeBearingDeg(deg: number): number {
  if (!Number.isFinite(deg)) return 0;
  return ((deg % 360) + 360) % 360;
}

export function segmentBearingDeg(from: LatLngPoint, to: LatLngPoint): number {
  const { x, y } = projectLatLngToMeters(to, from);
  if (Math.hypot(x, y) < 1e-6) return 0;
  // Azimuth: atan2(east, north)
  return normalizeBearingDeg(Math.atan2(x, y) * RAD_TO_DEG);
}

/* ─── 1. Study-Area Resolution for Boundary Workspace ───────────────── */

export interface ResolvedBoundaryStudyPolygon {
  key: string;
  kind: 'parcel' | 'layer';
  id: string;
  name: string;
  role: StudyGeometryRole;
  polygon: LatLngPoint[];
  /** Tuple `[lat, lng][]` representation for overlay renderers. */
  points: [number, number][];
  strokeColor: string;
  fillOpacity: number;
  source: 'study-area-layer' | 'study-area-parcel' | 'boundary';
  holes?: LatLngPoint[][];
  areaAcres?: number;
  pinnedHash: string;
  currentHash?: string;
  status: 'valid' | 'stale';
  staleMessage?: string;
  layerVisibleOnSite?: boolean;
  visible?: boolean;
}

export interface MissingBoundaryStudyRef {
  key: string;
  kind: 'parcel' | 'layer';
  id: string;
  pinnedHash: string;
  message: string;
}

export interface BoundaryWorkspaceStudyResolution {
  hasStudyArea: boolean;
  studyAreaName: string | null;
  revision: number | null;
  includedPolygons: ResolvedBoundaryStudyPolygon[];
  stalePolygons: ResolvedBoundaryStudyPolygon[];
  missingRefs: MissingBoundaryStudyRef[];
  hasStaleOrMissing: boolean;
}

/**
 * Resolves `site.studyArea.included` against all site parcels and layers
 * (regardless of `layer.visible !== false`) so the Boundary workspace can:
 *   1. Always render blue selected study-area polygons on the map even when
 *      a layer's normal visibility toggle is off,
 *   2. Distinguish selected study polygons from canonical editable site
 *      boundary parcels, and
 *   3. Surface stale or missing study-area pins clearly.
 */
export function resolveBoundaryWorkspaceStudySelection(
  site: StudyAreaSite,
): BoundaryWorkspaceStudyResolution {
  const selection = site.studyArea;
  if (!selection || !Array.isArray(selection.included) || selection.included.length === 0) {
    return {
      hasStudyArea: false,
      studyAreaName: selection?.name ?? null,
      revision: selection?.revision ?? null,
      includedPolygons: [],
      stalePolygons: [],
      missingRefs: [],
      hasStaleOrMissing: false,
    };
  }

  const candidates = listStudyAreaCandidates(site);
  const byKey = new Map(candidates.map(c => [studyGeometryKey(c), c]));
  const roleByKey = new Map(
    (selection.roles ?? []).map(r => [studyGeometryKey(r), r.role]),
  );
  const layerVisById = new Map(
    (site.siteLayers ?? []).map(l => [l.id, l.visible !== false]),
  );

  const includedPolygons: ResolvedBoundaryStudyPolygon[] = [];
  const stalePolygons: ResolvedBoundaryStudyPolygon[] = [];
  const missingRefs: MissingBoundaryStudyRef[] = [];

  for (const ref of selection.included) {
    const key = studyGeometryKey(ref);
    const candidate = byKey.get(key);
    const role = roleByKey.get(key) ?? 'development';

    if (!candidate || candidate.geometryType !== 'polygon' || candidate.polygon.length < 3) {
      missingRefs.push({
        key,
        kind: ref.kind,
        id: ref.id,
        pinnedHash: ref.geometryHash,
        message: `Study polygon ${candidate?.name ?? ref.id} (${key}) is missing or invalid. Review Study area before continuing.`,
      });
      continue;
    }

    const isStale = Boolean(
      !candidate.geometryHash || candidate.geometryHash !== ref.geometryHash,
    );
    const staleMessage = isStale
      ? candidate.geometryHash
        ? studyPolygonChangedMessage(candidate.name, ref.geometryHash, candidate.geometryHash)
        : `Study polygon ${candidate.name} (${key}) has invalid geometry: ${candidate.error ?? 'unknown error'}.`
      : undefined;

    const validPoly = candidate.polygon.filter(isValidPoint);
    const resolved: ResolvedBoundaryStudyPolygon = {
      key,
      kind: ref.kind,
      id: ref.id,
      name: candidate.name,
      role,
      polygon: validPoly,
      points: validPoly.map(p => [p.lat, p.lng] as [number, number]),
      strokeColor: '#2563eb',
      fillOpacity: 0.18,
      source: ref.kind === 'layer' ? 'study-area-layer' : 'study-area-parcel',
      ...(candidate.holes && candidate.holes.length > 0 ? { holes: candidate.holes } : {}),
      areaAcres: candidate.areaAcres,
      pinnedHash: ref.geometryHash,
      currentHash: candidate.geometryHash,
      status: isStale ? 'stale' : 'valid',
      ...(staleMessage ? { staleMessage } : {}),
      ...(ref.kind === 'layer' ? { layerVisibleOnSite: layerVisById.get(ref.id) ?? true } : {}),
    };

    includedPolygons.push(resolved);
    if (isStale) {
      stalePolygons.push(resolved);
    }
  }

  return {
    hasStudyArea: true,
    studyAreaName: selection.name ?? 'DC + BTM development area',
    revision: selection.revision ?? 1,
    includedPolygons,
    stalePolygons,
    missingRefs,
    hasStaleOrMissing: stalePolygons.length > 0 || missingRefs.length > 0,
  };
}

/**
 * Checks whether saving `nextParcels` as the canonical site boundary will
 * invalidate any parcel pins currently in `site.studyArea.included`.
 */
export function checkStaleStudyPinsAfterBoundaryChange(
  site: StudyAreaSite,
  nextParcels: readonly SiteBoundaryParcel[],
): {
  affectedRefs: Array<{ id: string; name: string; reason: 'modified' | 'removed'; message: string }>;
} {
  const includedParcelRefs = (site.studyArea?.included ?? []).filter(
    (ref): ref is StudyGeometryRef & { geometryHash: string } => ref.kind === 'parcel',
  );
  if (includedParcelRefs.length === 0) {
    return { affectedRefs: [] };
  }
  const nextById = new Map(nextParcels.map(p => [p.id, p]));
  const affectedRefs: Array<{ id: string; name: string; reason: 'modified' | 'removed'; message: string }> = [];

  for (const ref of includedParcelRefs) {
    const nextParcel = nextById.get(ref.id);
    if (!nextParcel) {
      affectedRefs.push({
        id: ref.id,
        name: ref.id,
        reason: 'removed',
        message: `Boundary parcel ${ref.id} is included in the study area and was removed. Review Study area to update the selection.`,
      });
      continue;
    }
    const evaluated = evaluateStudyPolygon(nextParcel.polygon, { holes: nextParcel.holes });
    const nextHash = 'geometryHash' in evaluated ? evaluated.geometryHash : undefined;
    if (!nextHash || nextHash !== ref.geometryHash) {
      affectedRefs.push({
        id: ref.id,
        name: nextParcel.label ?? ref.id,
        reason: 'modified',
        message: studyPolygonChangedMessage(
          nextParcel.label ?? ref.id,
          ref.geometryHash,
          nextHash ?? 'invalid',
        ),
      });
    }
  }

  return { affectedRefs };
}

/* ─── 2. Viewport Bounds & Fitting ──────────────────────────────────── */

export function computeBoundsFromPoints(points: readonly LatLngPoint[]): LatLngBoundsBox | null {
  const valid = points.filter(isValidPoint);
  if (valid.length === 0) return null;
  let north = -Infinity;
  let south = Infinity;
  let east = -Infinity;
  let west = Infinity;
  for (const p of valid) {
    if (p.lat > north) north = p.lat;
    if (p.lat < south) south = p.lat;
    if (p.lng > east) east = p.lng;
    if (p.lng < west) west = p.lng;
  }
  return { north, south, east, west };
}

export function estimateZoomForBounds(bounds: LatLngBoundsBox): number {
  const latSpan = Math.max(0, bounds.north - bounds.south);
  const midLat = (bounds.north + bounds.south) / 2;
  const lngSpan = Math.max(0, (bounds.east - bounds.west) * Math.max(0.2, Math.cos(midLat * DEG_TO_RAD)));
  const maxSpan = Math.max(latSpan, lngSpan);
  if (maxSpan <= 0.001) return 17;
  if (maxSpan <= 0.003) return 16;
  if (maxSpan <= 0.008) return 15;
  if (maxSpan <= 0.02) return 14;
  if (maxSpan <= 0.05) return 13;
  if (maxSpan <= 0.12) return 12;
  if (maxSpan <= 0.3) return 11;
  return 10;
}

export interface BoundaryWorkspaceViewport {
  bounds: LatLngBoundsBox | null;
  center: LatLngPoint;
  zoom: number;
  source: 'study-and-boundary' | 'study-only' | 'boundary-only' | 'layers-only' | 'site-location';
}

export function computeBoundaryWorkspaceViewport(
  site: StudyAreaSite & { location?: LatLngPoint; mapLayers?: StudyAreaSite['siteLayers'] },
  options?: {
    overrideParcels?: readonly SiteBoundaryParcel[];
  },
): BoundaryWorkspaceViewport {
  const studyPolys = resolveBoundaryStudyPolygons(site);
  const studyPoints = studyPolys.flatMap(p => p.polygon).filter(isValidPoint);

  const parcels = options?.overrideParcels ?? materializeSiteBoundaries(site);
  const boundaryPoints = parcels.flatMap(p => p.polygon).filter(isValidPoint);

  if (studyPoints.length > 0 && boundaryPoints.length > 0) {
    const bounds = computeBoundsFromPoints([...studyPoints, ...boundaryPoints])!;
    return {
      bounds,
      center: {
        lat: (bounds.north + bounds.south) / 2,
        lng: (bounds.east + bounds.west) / 2,
      },
      zoom: estimateZoomForBounds(bounds),
      source: 'study-and-boundary',
    };
  }

  if (studyPoints.length > 0) {
    const bounds = computeBoundsFromPoints(studyPoints)!;
    return {
      bounds,
      center: {
        lat: (bounds.north + bounds.south) / 2,
        lng: (bounds.east + bounds.west) / 2,
      },
      zoom: estimateZoomForBounds(bounds),
      source: 'study-only',
    };
  }

  if (boundaryPoints.length > 0) {
    const bounds = computeBoundsFromPoints(boundaryPoints)!;
    return {
      bounds,
      center: {
        lat: (bounds.north + bounds.south) / 2,
        lng: (bounds.east + bounds.west) / 2,
      },
      zoom: estimateZoomForBounds(bounds),
      source: 'boundary-only',
    };
  }

  const visibleLayerPoints = (site.siteLayers ?? [])
    .filter(l => l.visible !== false && l.geometry.type !== 'point')
    .flatMap(l => l.geometry.coordinates)
    .filter(isValidPoint);
  if (visibleLayerPoints.length > 0) {
    const bounds = computeBoundsFromPoints(visibleLayerPoints)!;
    return {
      bounds,
      center: {
        lat: (bounds.north + bounds.south) / 2,
        lng: (bounds.east + bounds.west) / 2,
      },
      zoom: estimateZoomForBounds(bounds),
      source: 'layers-only',
    };
  }

  const fallbackCenter = isValidPoint(site.siteCentroid)
    ? site.siteCentroid
    : isValidPoint(site.location)
      ? site.location
      : { lat: 39.5, lng: -98.35 };
  return {
    bounds: null,
    center: fallbackCenter,
    zoom: 15,
    source: 'site-location',
  };
}

export function fitMapToViewportBounds(
  map: unknown,
  boundsOrPoints:
    | LatLngBoundsBox
    | readonly ([number, number] | LatLngPoint)[]
    | null
    | undefined,
  paddingPx = 48,
): boolean {
  if (!map || !boundsOrPoints) return false;
  let bounds: LatLngBoundsBox | null = null;
  if (Array.isArray(boundsOrPoints)) {
    const pts: LatLngPoint[] = boundsOrPoints
      .map((item): LatLngPoint | null => {
        if (Array.isArray(item) && item.length >= 2) {
          return { lat: Number(item[0]), lng: Number(item[1]) };
        }
        if (item && typeof item === 'object' && 'lat' in item && 'lng' in item) {
          return { lat: Number((item as LatLngPoint).lat), lng: Number((item as LatLngPoint).lng) };
        }
        return null;
      })
      .filter((p): p is LatLngPoint => isValidPoint(p));
    bounds = computeBoundsFromPoints(pts);
  } else {
    bounds = boundsOrPoints as LatLngBoundsBox;
  }
  if (!bounds) return false;
  const mapObj = map as {
    fitBounds?: (b: LatLngBoundsBox, padding?: number) => void;
    panTo?: (c: LatLngPoint) => void;
    setZoom?: (z: number) => void;
  };
  if (typeof mapObj.fitBounds === 'function') {
    mapObj.fitBounds(bounds, paddingPx);
    return true;
  }
  const center = {
    lat: (bounds.north + bounds.south) / 2,
    lng: (bounds.east + bounds.west) / 2,
  };
  if (typeof mapObj.panTo === 'function') {
    mapObj.panTo(center);
  }
  if (typeof mapObj.setZoom === 'function') {
    mapObj.setZoom(estimateZoomForBounds(bounds));
  }
  return Boolean(mapObj.panTo || mapObj.setZoom);
}

/* ─── 3. Opt-In Vertex & Edge Snapping ──────────────────────────────── */

export interface SnapTargetPolygon {
  id: string;
  label: string;
  kind: 'parcel' | 'study-layer' | 'context-layer';
  polygon: readonly LatLngPoint[];
}

export interface SnapOptions {
  enabled: boolean;
  toleranceMeters?: number;
  snapToVertices?: boolean;
  snapToEdges?: boolean;
}

export interface SnapResult {
  point: LatLngPoint;
  snapped: boolean;
  snapKind: 'vertex' | 'edge' | null;
  targetId?: string;
  targetLabel?: string;
  targetKind?: SnapTargetPolygon['kind'];
  distanceMeters?: number;
  edgeBearingDeg?: number;
  guideLine?: [LatLngPoint, LatLngPoint];
}

export function snapPointToTargets(
  candidate: LatLngPoint,
  targets: readonly SnapTargetPolygon[],
  options: SnapOptions,
): SnapResult {
  if (!options.enabled || !isValidPoint(candidate) || targets.length === 0) {
    return { point: candidate, snapped: false, snapKind: null };
  }
  const tolerance = Math.max(0.5, options.toleranceMeters ?? 15);
  const checkVertices = options.snapToVertices !== false;
  const checkEdges = options.snapToEdges !== false;

  // Priority 1: nearest vertex within tolerance
  if (checkVertices) {
    let bestVertex: {
      point: LatLngPoint;
      dist: number;
      target: SnapTargetPolygon;
    } | null = null;

    for (const target of targets) {
      for (const v of target.polygon) {
        if (!isValidPoint(v)) continue;
        const dist = distanceMetersBetween(candidate, v);
        if (dist <= tolerance && (!bestVertex || dist < bestVertex.dist)) {
          bestVertex = { point: { lat: v.lat, lng: v.lng }, dist, target };
        }
      }
    }

    if (bestVertex) {
      return {
        point: bestVertex.point,
        snapped: true,
        snapKind: 'vertex',
        targetId: bestVertex.target.id,
        targetLabel: bestVertex.target.label,
        targetKind: bestVertex.target.kind,
        distanceMeters: Number(bestVertex.dist.toFixed(2)),
        guideLine: [candidate, bestVertex.point],
      };
    }
  }

  // Priority 2: nearest edge projection within tolerance
  if (checkEdges) {
    let bestEdge: {
      point: LatLngPoint;
      dist: number;
      bearingDeg: number;
      target: SnapTargetPolygon;
    } | null = null;

    for (const target of targets) {
      const pts = target.polygon.filter(isValidPoint);
      if (pts.length < 2) continue;
      for (let i = 0; i < pts.length; i++) {
        const a = pts[i]!;
        const b = pts[(i + 1) % pts.length]!;
        const aXY = { x: 0, y: 0 };
        const bXY = projectLatLngToMeters(b, a);
        const pXY = projectLatLngToMeters(candidate, a);
        const dx = bXY.x - aXY.x;
        const dy = bXY.y - aXY.y;
        const segLenSq = dx * dx + dy * dy;
        if (segLenSq < 1e-6) continue;
        const t = Math.max(0, Math.min(1, (pXY.x * dx + pXY.y * dy) / segLenSq));
        const projXY = { x: t * dx, y: t * dy };
        const dist = Math.hypot(pXY.x - projXY.x, pXY.y - projXY.y);
        if (dist <= tolerance && (!bestEdge || dist < bestEdge.dist)) {
          const projLatLng = unprojectMetersToLatLng(projXY, a);
          bestEdge = {
            point: projLatLng,
            dist,
            bearingDeg: segmentBearingDeg(a, b),
            target,
          };
        }
      }
    }

    if (bestEdge) {
      return {
        point: bestEdge.point,
        snapped: true,
        snapKind: 'edge',
        targetId: bestEdge.target.id,
        targetLabel: bestEdge.target.label,
        targetKind: bestEdge.target.kind,
        distanceMeters: Number(bestEdge.dist.toFixed(2)),
        edgeBearingDeg: bestEdge.bearingDeg,
        guideLine: [candidate, bestEdge.point],
      };
    }
  }

  return { point: candidate, snapped: false, snapKind: null };
}

/* ─── 4. Opt-In Alignment Guides & Orthogonal Segment Constraint ────── */

export interface AlignmentGuide {
  axis: 'parallel' | 'perpendicular';
  referenceVertex: LatLngPoint;
  alignedPoint: LatLngPoint;
  offsetMeters: number;
  bearingDeg: number;
}

export function computeAlignmentGuides(
  candidate: LatLngPoint,
  referenceVertices: readonly LatLngPoint[],
  options?: {
    enabled?: boolean;
    referenceBearingDeg?: number;
    toleranceMeters?: number;
  },
): {
  point: LatLngPoint;
  guides: AlignmentGuide[];
} {
  if (options?.enabled === false || !isValidPoint(candidate) || referenceVertices.length === 0) {
    return { point: candidate, guides: [] };
  }
  const bearingDeg = normalizeBearingDeg(options?.referenceBearingDeg ?? 0);
  const tolerance = Math.max(0.5, options?.toleranceMeters ?? 8);
  const rad = bearingDeg * DEG_TO_RAD;
  // Unit vector along reference bearing (u: parallel to bearing, v: perpendicular +90 deg)
  const ux = Math.sin(rad);
  const uy = Math.cos(rad);
  const vx = Math.cos(rad);
  const vy = -Math.sin(rad);

  const origin = candidate;
  const candUV = { u: 0, v: 0 };
  let bestParallel: { vertex: LatLngPoint; deltaV: number; uProj: number } | null = null;
  let bestPerpendicular: { vertex: LatLngPoint; deltaU: number; vProj: number } | null = null;

  for (const ref of referenceVertices) {
    if (!isValidPoint(ref)) continue;
    const xy = projectLatLngToMeters(ref, origin);
    if (Math.hypot(xy.x, xy.y) < 0.1) continue;
    const u = xy.x * ux + xy.y * uy;
    const v = xy.x * vx + xy.y * vy;
    // Aligned along parallel axis -> small |v|
    if (Math.abs(v) <= tolerance && (!bestParallel || Math.abs(v) < Math.abs(bestParallel.deltaV))) {
      bestParallel = { vertex: ref, deltaV: v, uProj: u };
    }
    // Aligned along perpendicular axis -> small |u|
    if (Math.abs(u) <= tolerance && (!bestPerpendicular || Math.abs(u) < Math.abs(bestPerpendicular.deltaU))) {
      bestPerpendicular = { vertex: ref, deltaU: u, vProj: v };
    }
  }

  let nextU = candUV.u;
  let nextV = candUV.v;
  if (bestParallel) nextV = bestParallel.deltaV;
  if (bestPerpendicular) nextU = bestPerpendicular.deltaU;

  const alignedXY = {
    x: nextU * ux + nextV * vx,
    y: nextU * uy + nextV * vy,
  };
  const alignedPoint =
    bestParallel || bestPerpendicular ? unprojectMetersToLatLng(alignedXY, origin) : candidate;

  const guides: AlignmentGuide[] = [];
  if (bestParallel) {
    guides.push({
      axis: 'parallel',
      referenceVertex: bestParallel.vertex,
      alignedPoint,
      offsetMeters: Number(Math.abs(bestParallel.deltaV).toFixed(2)),
      bearingDeg,
    });
  }
  if (bestPerpendicular) {
    guides.push({
      axis: 'perpendicular',
      referenceVertex: bestPerpendicular.vertex,
      alignedPoint,
      offsetMeters: Number(Math.abs(bestPerpendicular.deltaU).toFixed(2)),
      bearingDeg: normalizeBearingDeg(bearingDeg + 90),
    });
  }

  return { point: alignedPoint, guides };
}

export interface OrthogonalConstraintResult {
  point: LatLngPoint;
  constrainedBearingDeg: number;
  lengthMeters: number;
  axis: 'parallel' | 'perpendicular';
}

/**
 * Constrains `candidate` relative to `anchor` so the segment `anchor -> point`
 * lies strictly parallel (bearing or bearing + 180) or perpendicular
 * (bearing + 90 or bearing + 270) to `referenceBearingDeg`.
 *
 * When `closingAnchor` (e.g., first vertex of a 4-point rectangle) is also
 * supplied, projects the closing corner so `point -> closingAnchor` is
 * simultaneously orthogonal whenever the closing projection is non-degenerate.
 */
export function constrainPointOrthogonal(
  anchor: LatLngPoint,
  candidate: LatLngPoint,
  options?: {
    referenceBearingDeg?: number;
    closingAnchor?: LatLngPoint;
  },
): OrthogonalConstraintResult {
  const bearingDeg = normalizeBearingDeg(options?.referenceBearingDeg ?? 0);
  const rad = bearingDeg * DEG_TO_RAD;
  const ux = Math.sin(rad);
  const uy = Math.cos(rad);
  const vx = Math.cos(rad);
  const vy = -Math.sin(rad);

  const xy = projectLatLngToMeters(candidate, anchor);
  const u = xy.x * ux + xy.y * uy;
  const v = xy.x * vx + xy.y * vy;

  const useParallel = Math.abs(u) >= Math.abs(v);
  let projU = useParallel ? u : 0;
  let projV = useParallel ? 0 : v;

  if (options?.closingAnchor && isValidPoint(options.closingAnchor)) {
    const closeXY = projectLatLngToMeters(options.closingAnchor, anchor);
    const closeU = closeXY.x * ux + closeXY.y * uy;
    const closeV = closeXY.x * vx + closeXY.y * vy;
    if (useParallel && Math.abs(closeU) > 1) {
      projU = closeU;
    } else if (!useParallel && Math.abs(closeV) > 1) {
      projV = closeV;
    }
  }

  const outXY = {
    x: projU * ux + projV * vx,
    y: projU * uy + projV * vy,
  };
  const point = unprojectMetersToLatLng(outXY, anchor);
  const lengthMeters = Math.hypot(outXY.x, outXY.y);
  const constrainedBearingDeg = useParallel
    ? projU >= 0
      ? bearingDeg
      : normalizeBearingDeg(bearingDeg + 180)
    : projV >= 0
      ? normalizeBearingDeg(bearingDeg + 90)
      : normalizeBearingDeg(bearingDeg + 270);

  return {
    point,
    constrainedBearingDeg,
    lengthMeters: Number(lengthMeters.toFixed(2)),
    axis: useParallel ? 'parallel' : 'perpendicular',
  };
}

/* ─── 5. Opt-In Polygon Squaring / Orthogonal Rectification ─────────── */

/**
 * Computes the dominant edge bearing (in [0, 90) degrees) from a polygon's
 * longest edge so the user can align/square relative to an existing edge or
 * override with an explicit bearing.
 */
export function dominantPolygonBearingDeg(polygon: readonly LatLngPoint[]): number {
  const pts = polygon.filter(isValidPoint);
  if (pts.length < 2) return 0;
  let maxLen = -1;
  let bestBearing = 0;
  for (let i = 0; i < pts.length; i++) {
    const a = pts[i]!;
    const b = pts[(i + 1) % pts.length]!;
    const len = distanceMetersBetween(a, b);
    if (len > maxLen) {
      maxLen = len;
      bestBearing = segmentBearingDeg(a, b) % 90;
    }
  }
  return Number(bestBearing.toFixed(1));
}

export interface SquaredPolygonPreviewResult {
  ok: boolean;
  originalPolygon: LatLngPoint[];
  previewPolygon: LatLngPoint[];
  bearingDeg: number;
  beforeAreaAcres: number;
  afterAreaAcres: number;
  beforePerimeterKm: number;
  afterPerimeterKm: number;
  centroid: LatLngPoint;
  squaredEdgeCount: number;
  preservedObliqueEdgeCount: number;
  validationError?: string;
}

interface Line2D {
  /** Point on line in rotated (u, v) frame. */
  u0: number;
  v0: number;
  /** Unit direction vector in rotated (u, v) frame. */
  du: number;
  dv: number;
  kind: 'u-axis' | 'v-axis' | 'oblique';
}

function intersectLines2D(l1: Line2D, l2: Line2D): { u: number; v: number } | null {
  const det = l1.du * l2.dv - l1.dv * l2.du;
  if (Math.abs(det) < 1e-6) return null;
  const deltaU = l2.u0 - l1.u0;
  const deltaV = l2.v0 - l1.v0;
  const t = (deltaU * l2.dv - deltaV * l2.du) / det;
  return {
    u: l1.u0 + t * l1.du,
    v: l1.v0 + t * l1.dv,
  };
}

/**
 * Computes an opt-in squared / orthogonalized preview for `polygon` relative
 * to `options.bearingDeg` (default: dominant edge bearing).
 *
 * - Edges within `maxEdgeAngleDeviationDeg` (default 32°) of the parallel or
 *   perpendicular axes of `bearingDeg` are snapped to exact 0° / 90° lines
 *   passing through the edge midpoint.
 * - Oblique edges on irregular polygons (deviation > `maxEdgeAngleDeviationDeg`)
 *   preserve their original angle so non-rectangular parcels (e.g. diagonal
 *   rail/river frontage) are not crushed into a bounding box.
 * - For 4-vertex near-rectangular polygons where all 4 edges orthogonalize into
 *   an alternating rectangle, fits an exact axis-aligned rectangle in the
 *   rotated frame preserving centroid and area.
 * - Always validates the resulting polygon via `evaluateStudyPolygon`; if
 *   squaring would create a self-intersection or invalid ring, returns
 *   `ok: false` with `validationError` and leaves `originalPolygon` untouched.
 */
export function computeSquaredPolygonPreview(
  polygon: readonly LatLngPoint[],
  options?: {
    bearingDeg?: number;
    maxEdgeAngleDeviationDeg?: number;
    preserveArea?: boolean;
  },
): SquaredPolygonPreviewResult {
  const pts = polygon.filter(isValidPoint).map(p => ({ lat: p.lat, lng: p.lng }));
  const beforeAreaAcres = pts.length >= 3 ? Number(polygonArea(pts).toFixed(2)) : 0;
  const beforePerimeterKm = pts.length >= 3 ? Number(polygonPerimeter(pts).toFixed(3)) : 0;
  const origin = pts.length >= 3 ? polygonCentroid(pts) : pts[0] ?? { lat: 0, lng: 0 };

  if (pts.length < 3) {
    return {
      ok: false,
      originalPolygon: pts,
      previewPolygon: pts,
      bearingDeg: 0,
      beforeAreaAcres,
      afterAreaAcres: beforeAreaAcres,
      beforePerimeterKm,
      afterPerimeterKm: beforePerimeterKm,
      centroid: origin,
      squaredEdgeCount: 0,
      preservedObliqueEdgeCount: 0,
      validationError: 'Polygon must have at least 3 vertices to align or square.',
    };
  }

  const bearingDeg = normalizeBearingDeg(
    options?.bearingDeg !== undefined ? options.bearingDeg : dominantPolygonBearingDeg(pts),
  );
  const maxDevDeg = Math.max(5, Math.min(45, options?.maxEdgeAngleDeviationDeg ?? 32));
  const rad = bearingDeg * DEG_TO_RAD;
  const ux = Math.sin(rad);
  const uy = Math.cos(rad);
  const vx = Math.cos(rad);
  const vy = -Math.sin(rad);

  const uvPts = pts.map(p => {
    const xy = projectLatLngToMeters(p, origin);
    return {
      u: xy.x * ux + xy.y * uy,
      v: xy.x * vx + xy.y * vy,
    };
  });

  const lines: Line2D[] = [];
  let squaredEdgeCount = 0;
  let preservedObliqueEdgeCount = 0;

  for (let i = 0; i < uvPts.length; i++) {
    const a = uvPts[i]!;
    const b = uvPts[(i + 1) % uvPts.length]!;
    const duRaw = b.u - a.u;
    const dvRaw = b.v - a.v;
    const len = Math.hypot(duRaw, dvRaw);
    const midU = (a.u + b.u) / 2;
    const midV = (a.v + b.v) / 2;
    if (len < 1e-4) {
      lines.push({ u0: midU, v0: midV, du: 1, dv: 0, kind: 'u-axis' });
      continue;
    }
    const du = duRaw / len;
    const dv = dvRaw / len;
    // Angle relative to u-axis (0 or 180 deg) vs v-axis (90 or 270 deg)
    const angleToUDeg = Math.acos(Math.min(1, Math.abs(du))) * RAD_TO_DEG;
    const angleToVDeg = Math.acos(Math.min(1, Math.abs(dv))) * RAD_TO_DEG;

    if (angleToUDeg <= maxDevDeg) {
      lines.push({
        u0: midU,
        v0: midV,
        du: du >= 0 ? 1 : -1,
        dv: 0,
        kind: 'u-axis',
      });
      squaredEdgeCount += 1;
    } else if (angleToVDeg <= maxDevDeg) {
      lines.push({
        u0: midU,
        v0: midV,
        du: 0,
        dv: dv >= 0 ? 1 : -1,
        kind: 'v-axis',
      });
      squaredEdgeCount += 1;
    } else {
      lines.push({
        u0: midU,
        v0: midV,
        du,
        dv,
        kind: 'oblique',
      });
      preservedObliqueEdgeCount += 1;
    }
  }

  let candidateUV: Array<{ u: number; v: number }> = [];

  // Special case: 4-vertex quad where all 4 edges squared (or user requested squaring a quad)
  const isAllSquaredQuad =
    uvPts.length === 4 &&
    preservedObliqueEdgeCount === 0 &&
    lines.some(l => l.kind === 'u-axis') &&
    lines.some(l => l.kind === 'v-axis');

  if (isAllSquaredQuad) {
    // Intersect consecutive edges; if consecutive edges were classified onto the same axis,
    // use the rotated bounding box scaled to preserve area.
    const intersected: Array<{ u: number; v: number } | null> = [];
    for (let i = 0; i < 4; i++) {
      const prevLine = lines[(i + 3) % 4]!;
      const currLine = lines[i]!;
      intersected.push(intersectLines2D(prevLine, currLine));
    }
    if (intersected.every((p): p is { u: number; v: number } => p !== null)) {
      candidateUV = intersected;
    } else {
      const uVals = uvPts.map(p => p.u);
      const vVals = uvPts.map(p => p.v);
      const minU = Math.min(...uVals);
      const maxU = Math.max(...uVals);
      const minV = Math.min(...vVals);
      const maxV = Math.max(...vVals);
      candidateUV = [
        { u: minU, v: minV },
        { u: maxU, v: minV },
        { u: maxU, v: maxV },
        { u: minU, v: maxV },
      ];
    }
  } else {
    // General N-gon: intersect consecutive edge lines (prevLine, currLine);
    // if two consecutive edges are parallel (collinear/step), project the original vertex onto the average line.
    for (let i = 0; i < uvPts.length; i++) {
      const prevLine = lines[(i + uvPts.length - 1) % uvPts.length]!;
      const currLine = lines[i]!;
      const hit = intersectLines2D(prevLine, currLine);
      if (hit) {
        candidateUV.push(hit);
      } else {
        const orig = uvPts[i]!;
        candidateUV.push({ u: orig.u, v: orig.v });
      }
    }
  }

  // Convert back to LatLng
  let previewPolygon = candidateUV.map(uv =>
    unprojectMetersToLatLng(
      {
        x: uv.u * ux + uv.v * vx,
        y: uv.u * uy + uv.v * vy,
      },
      origin,
    ),
  );

  // Optional area preservation scaling around centroid when all edges were squared
  if (
    options?.preserveArea !== false &&
    preservedObliqueEdgeCount === 0 &&
    beforeAreaAcres > 0
  ) {
    const rawAfterArea = polygonArea(previewPolygon);
    if (rawAfterArea > 0) {
      const ratio = beforeAreaAcres / rawAfterArea;
      // Scale within reasonable bounds (0.7 to 1.3) to keep exact acreage
      if (ratio >= 0.7 && ratio <= 1.3 && Math.abs(ratio - 1) > 1e-4) {
        const scale = Math.sqrt(ratio);
        const cUV = {
          u: candidateUV.reduce((s, p) => s + p.u, 0) / candidateUV.length,
          v: candidateUV.reduce((s, p) => s + p.v, 0) / candidateUV.length,
        };
        candidateUV = candidateUV.map(p => ({
          u: cUV.u + (p.u - cUV.u) * scale,
          v: cUV.v + (p.v - cUV.v) * scale,
        }));
        previewPolygon = candidateUV.map(uv =>
          unprojectMetersToLatLng(
            {
              x: uv.u * ux + uv.v * vx,
              y: uv.u * uy + uv.v * vy,
            },
            origin,
          ),
        );
      }
    }
  }

  const evaluation = evaluateStudyPolygon(previewPolygon);
  if ('error' in evaluation) {
    return {
      ok: false,
      originalPolygon: pts,
      previewPolygon: pts,
      bearingDeg,
      beforeAreaAcres,
      afterAreaAcres: beforeAreaAcres,
      beforePerimeterKm,
      afterPerimeterKm: beforePerimeterKm,
      centroid: origin,
      squaredEdgeCount,
      preservedObliqueEdgeCount,
      validationError: evaluation.error,
    };
  }

  const afterAreaAcres = Number(polygonArea(previewPolygon).toFixed(2));
  const afterPerimeterKm = Number(polygonPerimeter(previewPolygon).toFixed(3));
  const nextCentroid = polygonCentroid(previewPolygon);

  return {
    ok: true,
    originalPolygon: pts,
    previewPolygon,
    bearingDeg,
    beforeAreaAcres,
    afterAreaAcres,
    beforePerimeterKm,
    afterPerimeterKm,
    centroid: nextCentroid,
    squaredEdgeCount,
    preservedObliqueEdgeCount,
  };
}

/* ─── 6. High-Level Drawer & Editor Integration Helpers ─────────────── */

export interface SnapGuide {
  kind: 'vertex' | 'edge' | 'horizontal' | 'vertical' | 'parallel' | 'orthogonal';
  from: [number, number];
  to: [number, number];
  label?: string;
}

export interface SquareCornersPreview {
  originalPoints: [number, number][];
  squaredPoints: [number, number][];
  beforeMaxRightAngleDeviationDeg: number;
  afterMaxRightAngleDeviationDeg: number;
  areaDeltaPct: number;
}

export interface BoundaryHistoryState<T> {
  past: T[];
  present: T;
  future: T[];
}

export function createBoundaryHistory<T>(initial: T): BoundaryHistoryState<T> {
  return {
    past: [],
    present: structuredClone(initial),
    future: [],
  };
}

export function pushBoundaryHistory<T>(
  state: BoundaryHistoryState<T>,
  next: T,
  maxDepth = 50,
): BoundaryHistoryState<T> {
  if (JSON.stringify(state.present) === JSON.stringify(next)) {
    return state;
  }
  return {
    past: [...state.past.slice(-(maxDepth - 1)), structuredClone(state.present)],
    present: structuredClone(next),
    future: [],
  };
}

export function undoBoundaryHistory<T>(state: BoundaryHistoryState<T>): BoundaryHistoryState<T> {
  if (state.past.length === 0) return state;
  const previous = state.past[state.past.length - 1]!;
  return {
    past: state.past.slice(0, -1),
    present: structuredClone(previous),
    future: [structuredClone(state.present), ...state.future],
  };
}

export function redoBoundaryHistory<T>(state: BoundaryHistoryState<T>): BoundaryHistoryState<T> {
  if (state.future.length === 0) return state;
  const next = state.future[0]!;
  return {
    past: [...state.past, structuredClone(state.present)],
    present: structuredClone(next),
    future: state.future.slice(1),
  };
}

function maxRightAngleDeviationDeg(pts: readonly [number, number][]): number {
  if (pts.length < 3) return 0;
  const latLngs = pts.map(([lat, lng]) => ({ lat, lng }));
  const origin = polygonCentroid(latLngs);
  const xy = latLngs.map(p => projectLatLngToMeters(p, origin));
  let maxDev = 0;
  for (let i = 0; i < xy.length; i++) {
    const prev = xy[(i - 1 + xy.length) % xy.length]!;
    const curr = xy[i]!;
    const next = xy[(i + 1) % xy.length]!;
    const u = { x: prev.x - curr.x, y: prev.y - curr.y };
    const v = { x: next.x - curr.x, y: next.y - curr.y };
    const lenU = Math.hypot(u.x, u.y);
    const lenV = Math.hypot(v.x, v.y);
    if (lenU < 1e-4 || lenV < 1e-4) continue;
    const cosTheta = Math.max(-1, Math.min(1, (u.x * v.x + u.y * v.y) / (lenU * lenV)));
    const angleDeg = Math.acos(cosTheta) * RAD_TO_DEG;
    const dev = Math.abs(angleDeg - 90);
    if (dev > maxDev) maxDev = dev;
  }
  return Number(maxDev.toFixed(1));
}

export function previewSquarePolygonCorners(
  points: readonly [number, number][],
): SquareCornersPreview {
  const cleanOriginal = points.map(([lat, lng]) => [lat, lng] as [number, number]);
  const latLngs = cleanOriginal.map(([lat, lng]) => ({ lat, lng }));
  const res = computeSquaredPolygonPreview(latLngs, { preserveArea: true });
  const squaredPoints = (res.ok ? res.previewPolygon : res.originalPolygon).map(
    p => [p.lat, p.lng] as [number, number],
  );
  const beforeDev = maxRightAngleDeviationDeg(cleanOriginal);
  const afterDev = maxRightAngleDeviationDeg(squaredPoints);
  const areaDeltaPct =
    res.beforeAreaAcres > 0
      ? Number(
          (
            (Math.abs(res.afterAreaAcres - res.beforeAreaAcres) / res.beforeAreaAcres) *
            100
          ).toFixed(2),
        )
      : 0;
  return {
    originalPoints: cleanOriginal,
    squaredPoints,
    beforeMaxRightAngleDeviationDeg: beforeDev,
    afterMaxRightAngleDeviationDeg: afterDev,
    areaDeltaPct,
  };
}

export function previewAxisAlignedRectangle(
  points: readonly [number, number][],
  orientToPrincipalAxis = true,
): SquareCornersPreview {
  const cleanOriginal = points.map(([lat, lng]) => [lat, lng] as [number, number]);
  if (cleanOriginal.length < 3) {
    return {
      originalPoints: cleanOriginal,
      squaredPoints: cleanOriginal,
      beforeMaxRightAngleDeviationDeg: 0,
      afterMaxRightAngleDeviationDeg: 0,
      areaDeltaPct: 0,
    };
  }
  const latLngs = cleanOriginal.map(([lat, lng]) => ({ lat, lng }));
  const origin = polygonCentroid(latLngs);
  const bearingDeg = orientToPrincipalAxis ? dominantPolygonBearingDeg(latLngs) : 0;
  const rad = bearingDeg * DEG_TO_RAD;
  const ux = Math.sin(rad);
  const uy = Math.cos(rad);
  const vx = Math.cos(rad);
  const vy = -Math.sin(rad);
  const uv = latLngs.map(p => {
    const xy = projectLatLngToMeters(p, origin);
    return {
      u: xy.x * ux + xy.y * uy,
      v: xy.x * vx + xy.y * vy,
    };
  });
  const minU = Math.min(...uv.map(p => p.u));
  const maxU = Math.max(...uv.map(p => p.u));
  const minV = Math.min(...uv.map(p => p.v));
  const maxV = Math.max(...uv.map(p => p.v));
  const rectUV = [
    { u: minU, v: minV },
    { u: maxU, v: minV },
    { u: maxU, v: maxV },
    { u: minU, v: maxV },
  ];
  const rectLatLng = rectUV.map(c =>
    unprojectMetersToLatLng(
      {
        x: c.u * ux + c.v * vx,
        y: c.u * uy + c.v * vy,
      },
      origin,
    ),
  );
  const squaredPoints = rectLatLng.map(p => [p.lat, p.lng] as [number, number]);
  const beforeArea = polygonArea(latLngs);
  const afterArea = polygonArea(rectLatLng);
  const areaDeltaPct =
    beforeArea > 0
      ? Number(((Math.abs(afterArea - beforeArea) / beforeArea) * 100).toFixed(2))
      : 0;
  return {
    originalPoints: cleanOriginal,
    squaredPoints,
    beforeMaxRightAngleDeviationDeg: maxRightAngleDeviationDeg(cleanOriginal),
    afterMaxRightAngleDeviationDeg: maxRightAngleDeviationDeg(squaredPoints),
    areaDeltaPct,
  };
}

export function snapAndAlignVertex(args: {
  candidate: [number, number];
  activePolygon?: readonly [number, number][];
  activeVertexIndex?: number;
  prevVertex?: [number, number];
  nextVertex?: [number, number];
  referencePolygons?: readonly (readonly [number, number][])[];
  snapToVerticesAndEdges?: boolean;
  enableAlignmentGuides?: boolean;
  enableOrthogonalConstraint?: boolean;
  vertexSnapToleranceM?: number;
  edgeSnapToleranceM?: number;
}): {
  point: [number, number];
  snapped: boolean;
  guides: SnapGuide[];
} {
  const candLatLng: LatLngPoint = { lat: args.candidate[0], lng: args.candidate[1] };
  const guides: SnapGuide[] = [];
  let pointLatLng = candLatLng;
  let snapped = false;

  if (args.snapToVerticesAndEdges) {
    const targets: SnapTargetPolygon[] = (args.referencePolygons ?? []).map((poly, idx) => ({
      id: `ref-${idx}`,
      label: `Reference ${idx + 1}`,
      kind: 'study-layer',
      polygon: poly.map(([lat, lng]) => ({ lat, lng })),
    }));
    if (args.activePolygon && args.activePolygon.length >= 3) {
      const filtered = args.activePolygon.filter((_, i) => i !== args.activeVertexIndex);
      targets.push({
        id: 'active',
        label: 'Active Polygon',
        kind: 'parcel',
        polygon: filtered.map(([lat, lng]) => ({ lat, lng })),
      });
    }
    const snapRes = snapPointToTargets(candLatLng, targets, {
      enabled: true,
      toleranceMeters: args.vertexSnapToleranceM ?? args.edgeSnapToleranceM ?? 15,
    });
    if (snapRes.snapped) {
      pointLatLng = snapRes.point;
      snapped = true;
      if (snapRes.guideLine) {
        guides.push({
          kind: snapRes.snapKind === 'vertex' ? 'vertex' : 'edge',
          from: [snapRes.guideLine[0].lat, snapRes.guideLine[0].lng],
          to: [snapRes.guideLine[1].lat, snapRes.guideLine[1].lng],
          label: snapRes.targetLabel,
        });
      }
    }
  }

  if (!snapped && args.enableOrthogonalConstraint && args.prevVertex) {
    const anchor: LatLngPoint = { lat: args.prevVertex[0], lng: args.prevVertex[1] };
    const closing = args.nextVertex
      ? { lat: args.nextVertex[0], lng: args.nextVertex[1] }
      : undefined;
    const ortho = constrainPointOrthogonal(anchor, pointLatLng, {
      closingAnchor: closing,
    });
    pointLatLng = ortho.point;
    snapped = true;
    guides.push({
      kind: 'orthogonal',
      from: [anchor.lat, anchor.lng],
      to: [pointLatLng.lat, pointLatLng.lng],
      label: '90° Ortho',
    });
  }

  if (!snapped && args.enableAlignmentGuides) {
    const refVerts: LatLngPoint[] = [
      ...(args.activePolygon ?? [])
        .filter((_, i) => i !== args.activeVertexIndex)
        .map(([lat, lng]) => ({ lat, lng })),
      ...(args.referencePolygons ?? []).flatMap(poly =>
        poly.map(([lat, lng]) => ({ lat, lng })),
      ),
    ];
    const alignRes = computeAlignmentGuides(pointLatLng, refVerts, { enabled: true });
    if (alignRes.guides.length > 0) {
      pointLatLng = alignRes.point;
      snapped = true;
      for (const g of alignRes.guides) {
        guides.push({
          kind: g.axis === 'parallel' ? 'vertical' : 'horizontal',
          from: [g.referenceVertex.lat, g.referenceVertex.lng],
          to: [g.alignedPoint.lat, g.alignedPoint.lng],
        });
      }
    }
  }

  return {
    point: [pointLatLng.lat, pointLatLng.lng],
    snapped,
    guides,
  };
}

/**
 * Resolves all valid blue study-area polygons for the Boundary / Multi-Parcel drawers
 * from the authoritative `site.studyArea.included` selection (requiring matching
 * `geometryHash` pins) regardless of `layer.visible` (#244 #issuecomment-6607632578,
 * #issuecomment-6609872698).
 */
export function resolveBoundaryStudyPolygons(
  site: (StudyAreaSite & { mapLayers?: StudyAreaSite['siteLayers'] }) | null | undefined,
): ResolvedBoundaryStudyPolygon[] {
  if (!site) return [];
  return resolveBoundaryWorkspaceStudySelection(site).includedPolygons.filter(
    p => p.status === 'valid',
  );
}

/**
 * Computes `[lat, lng][]` viewport points across all boundary parcels, study-area-selected
 * polygons, and visible map layers so the Boundary / Multi-Parcel drawer fits the union
 * of all relevant site geometry rather than stranding on a missing or stale pin.
 */
export function resolveBoundaryEditorViewportPoints(
  site: (StudyAreaSite & {
    location?: LatLngPoint | null;
    mapLayers?: StudyAreaSite['siteLayers'];
  }) | null | undefined,
): [number, number][] {
  if (!site) return [];
  const studyPolys = resolveBoundaryWorkspaceStudySelection(site).includedPolygons;
  const parcels = materializeSiteBoundaries(site);
  const allLayers = [...(site.siteLayers ?? []), ...(site.mapLayers ?? [])];
  const visibleLayerPts = allLayers
    .filter(l => l && l.visible !== false && l.geometry?.type !== 'point')
    .flatMap(l => l.geometry?.coordinates ?? [])
    .filter(isValidPoint)
    .map(p => [p.lat, p.lng] as [number, number]);

  const combined: [number, number][] = [
    ...studyPolys.flatMap(p => p.points),
    ...parcels.flatMap(p => p.polygon.filter(isValidPoint).map(pt => [pt.lat, pt.lng] as [number, number])),
    ...visibleLayerPts,
  ];
  if (combined.length > 0) return combined;

  if (isValidPoint(site.siteCentroid)) {
    return [[site.siteCentroid.lat, site.siteCentroid.lng]];
  }
  if (isValidPoint(site.location)) {
    return [[site.location.lat, site.location.lng]];
  }
  return [];
}

export function detectStaleOrMissingPinWarning(
  site: (StudyAreaSite & {
    location?: LatLngPoint | null;
    mapLayers?: StudyAreaSite['siteLayers'];
  }) | null | undefined,
  staleThresholdMeters = 2_500,
): {
  hasWarning: boolean;
  kind: 'none' | 'missing-pin' | 'stale-pin' | 'stale-study-area';
  distanceMeters: number | null;
  suggestedCentroid: [number, number] | null;
  message: string | null;
} {
  if (!site) {
    return {
      hasWarning: false,
      kind: 'none',
      distanceMeters: null,
      suggestedCentroid: null,
      message: null,
    };
  }
  const studyRes = resolveBoundaryWorkspaceStudySelection(site);
  const viewportPts = resolveBoundaryEditorViewportPoints(site);
  const bounds = computeBoundsFromPoints(
    viewportPts.map(([lat, lng]) => ({ lat, lng })),
  );
  const suggestedCentroid: [number, number] | null = bounds
    ? [
        Number(((bounds.north + bounds.south) / 2).toFixed(6)),
        Number(((bounds.east + bounds.west) / 2).toFixed(6)),
      ]
    : null;

  if (studyRes.hasStaleOrMissing) {
    const firstMsg =
      studyRes.stalePolygons[0]?.staleMessage ??
      studyRes.missingRefs[0]?.message ??
      'One or more study-area polygons were modified or removed since being pinned.';
    return {
      hasWarning: true,
      kind: 'stale-study-area',
      distanceMeters: null,
      suggestedCentroid,
      message: firstMsg,
    };
  }

  if (!suggestedCentroid) {
    return {
      hasWarning: false,
      kind: 'none',
      distanceMeters: null,
      suggestedCentroid: null,
      message: null,
    };
  }

  const loc = site.location;
  const hasValidPin =
    loc != null &&
    isValidPoint(loc) &&
    !(Math.abs(loc.lat) < 0.01 && Math.abs(loc.lng) < 0.01);

  if (!hasValidPin) {
    return {
      hasWarning: true,
      kind: 'missing-pin',
      distanceMeters: null,
      suggestedCentroid,
      message:
        'Site pin coordinates are missing while boundary/study-area layers are present. Map viewport has been fitted to the study area.',
    };
  }

  const distM = distanceMetersBetween(loc, {
    lat: suggestedCentroid[0],
    lng: suggestedCentroid[1],
  });
  if (distM > staleThresholdMeters) {
    return {
      hasWarning: true,
      kind: 'stale-pin',
      distanceMeters: Math.round(distM),
      suggestedCentroid,
      message: `Site pin is ${(distM / 1000).toFixed(1)} km from the boundary / study-area center. Map viewport has been fitted to the study area.`,
    };
  }

  return {
    hasWarning: false,
    kind: 'none',
    distanceMeters: Math.round(distM),
    suggestedCentroid,
    message: null,
  };
}

export function canSwitchToMultiParcelMode(
  site: (StudyAreaSite & { mapLayers?: StudyAreaSite['siteLayers'] }) | null | undefined,
): boolean {
  if (!site) return true;
  return true;
}

/**
 * Builds the initial canonical parcel list when switching to Multi-Parcel mode.
 * Strictly materializes only canonical site boundaries (`siteBoundaries` / `siteBoundary`)
 * and never promotes or copies a study-area layer into a canonical boundary parcel
 * (#244 #issuecomment-6609872698).
 */
export function buildInitialParcelsForMultiParcelSwitch(
  site: (StudyAreaSite & { mapLayers?: StudyAreaSite['siteLayers'] }) | null | undefined,
): SiteBoundaryParcel[] {
  if (!site) return [];
  return materializeSiteBoundaries(site);
}
